import { ReactElement } from 'react';

import { Tooltip } from '@/design-system';
import TokenIcon from '@/icons/TokenIcon.svg?react';
import { getTokenUsage } from '@/utils/messages';
import { cn } from '@/utils/tailwind';
import { utcTime } from '@/utils/utc-time';

import { RunSchema } from '../../../types/schema';

const formatter = new Intl.NumberFormat('en-US', {});
const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 20,
  maximumSignificantDigits: 10,
});
const shorterUsdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
});

export function CostChip({
  run,
  isLoading,
}: {
  run: RunSchema | null | undefined;
  isLoading?: boolean;
}) {
  return (
    <RawCostChip
      totalTokens={run?.total_tokens}
      promptTokens={run?.prompt_tokens}
      completionTokens={run?.completion_tokens}
      promptCost={run?.prompt_cost}
      completionCost={run?.completion_cost}
      timeToFirstTokenMs={utcTime(run?.first_token_time).diff(
        utcTime(run?.start_time),
        'milliseconds'
      )}
      totalCost={run?.total_cost}
      isLoading={isLoading}
    />
  );
}

export function RawCostChip({
  totalTokens,
  promptTokens,
  completionTokens,
  audioTokens,
  cacheReadTokens,
  cacheCreationTokens,
  reasoningTokens,
  promptCost,
  completionCost,
  totalCost,
  isLoading,
  simple,
}: RawChipsTokenProps & {
  isLoading?: boolean;
  simple?: boolean;
}) {
  if (isLoading) {
    return (
      <div
        className={cn(
          'flex w-fit items-center gap-1 whitespace-nowrap rounded-md border border-secondary bg-background px-1 py-0.5 text-xs',
          simple && 'border-none'
        )}
      >
        <span>...</span>
      </div>
    );
  }

  return totalCost != null ? (
    <RawTokensTooltip
      promptCost={promptCost}
      completionCost={completionCost}
      totalTokens={totalTokens}
      promptTokens={promptTokens}
      completionTokens={completionTokens}
      audioTokens={audioTokens}
      cacheReadTokens={cacheReadTokens}
      cacheCreationTokens={cacheCreationTokens}
      reasoningTokens={reasoningTokens}
    >
      <div
        className={cn(
          'flex w-fit items-center gap-1 whitespace-nowrap rounded-md border border-secondary bg-background px-1 py-0.5 text-xs',
          simple && 'border-none'
        )}
      >
        {shorterUsdFormatter.format(totalCost)}
      </div>
    </RawTokensTooltip>
  ) : null;
}

export function TokensChip({
  run,
  isLoading,
}: {
  run: RunSchema | null | undefined;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-1 whitespace-nowrap rounded-md border border-secondary bg-background px-1 py-0.5 text-xs">
        <TokenIcon />
        <span>...</span>
      </div>
    );
  }

  return run?.total_tokens != null ? (
    <TokensTooltip run={run}>
      <div className="flex items-center gap-1 whitespace-nowrap rounded-md border border-secondary bg-background px-1 py-0.5 text-xs">
        <TokenIcon />
        {formatter.format(run.total_tokens)}
      </div>
    </TokensTooltip>
  ) : null;
}

type RawChipsTokenProps = {
  totalTokens?: number | null;
  promptTokens?: number | null;
  completionTokens?: number | null;
  audioTokens?: number | null;
  cacheReadTokens?: number | null;
  cacheCreationTokens?: number | null;
  reasoningTokens?: number | null;
  promptCost?: number | null;
  completionCost?: number | null;
  timeToFirstTokenMs?: number | null;
  totalCost?: number | null;
  promptTokenDetails?: Record<string, number> | null;
  completionTokenDetails?: Record<string, number> | null;
  promptCostDetails?: Record<string, number> | null;
  completionCostDetails?: Record<string, number> | null;
};

export function RawTokensChip({
  totalTokens,
  promptTokens,
  completionTokens,
  audioTokens,
  cacheReadTokens,
  cacheCreationTokens,
  reasoningTokens,
  promptCost,
  completionCost,
  promptTokenDetails,
  completionTokenDetails,
  promptCostDetails,
  completionCostDetails,
  isLoading,
  simple,
}: RawChipsTokenProps & {
  isLoading?: boolean;
  simple?: boolean;
}) {
  if (isLoading) {
    return (
      <div
        className={cn(
          'flex w-fit items-center gap-1 whitespace-nowrap rounded-md border border-secondary bg-background px-1 py-0.5 text-xs',
          simple && 'border-none'
        )}
      >
        <TokenIcon />
        <span>...</span>
      </div>
    );
  }

  return totalTokens != null ? (
    <RawTokensTooltip
      totalTokens={totalTokens}
      promptTokens={promptTokens}
      completionTokens={completionTokens}
      audioTokens={audioTokens}
      cacheReadTokens={cacheReadTokens}
      cacheCreationTokens={cacheCreationTokens}
      reasoningTokens={reasoningTokens}
      promptCost={promptCost}
      completionCost={completionCost}
      promptTokenDetails={promptTokenDetails}
      completionTokenDetails={completionTokenDetails}
      promptCostDetails={promptCostDetails}
      completionCostDetails={completionCostDetails}
    >
      <div
        className={cn(
          'flex w-fit items-center gap-1 whitespace-nowrap rounded-md border border-secondary bg-background px-1 py-0.5 text-xs',
          simple && 'border-none'
        )}
      >
        {simple ? null : <TokenIcon />}
        {formatter.format(totalTokens)}
      </div>
    </RawTokensTooltip>
  ) : null;
}

export function TokensTooltip({
  run,
  children,
}: {
  run: RunSchema;
  children: ReactElement;
}) {
  const { cacheReadTokens, cacheCreationTokens, reasoningTokens, audioTokens } =
    getTokenUsage(run);

  return run.total_tokens != null ? (
    <RawTokensTooltip
      totalTokens={run.total_tokens}
      promptTokens={run.prompt_tokens}
      completionTokens={run.completion_tokens}
      cacheReadTokens={cacheReadTokens}
      cacheCreationTokens={cacheCreationTokens}
      reasoningTokens={reasoningTokens}
      audioTokens={audioTokens}
      promptCost={run.prompt_cost}
      completionCost={run.completion_cost}
      promptTokenDetails={run.prompt_token_details}
      completionTokenDetails={run.completion_token_details}
      promptCostDetails={run.prompt_cost_details}
      completionCostDetails={run.completion_cost_details}
    >
      {children}
    </RawTokensTooltip>
  ) : null;
}

export function RawTokensTooltip({
  totalTokens,
  promptTokens,
  completionTokens,
  cacheReadTokens,
  cacheCreationTokens,
  reasoningTokens,
  audioTokens,
  promptCost,
  completionCost,

  children,
  promptTokenDetails,
  completionTokenDetails,
  promptCostDetails,
  completionCostDetails,
  side,
}: {
  totalTokens?: number | null;
  promptTokens?: number | null;
  completionTokens?: number | null;
  cacheReadTokens?: number | null;
  cacheCreationTokens?: number | null;
  reasoningTokens?: number | null;
  audioTokens?: number | null;
  promptCost?: number | null;
  completionCost?: number | null;

  children: ReactElement;
  promptTokenDetails?: Record<string, number> | null;
  completionTokenDetails?: Record<string, number> | null;
  promptCostDetails?: Record<string, number> | null;
  completionCostDetails?: Record<string, number> | null;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  const hasOneSubTokenCount =
    [
      audioTokens,
      promptTokens,
      completionTokens,
      reasoningTokens,
      cacheReadTokens,
      cacheCreationTokens,
    ].some((tokenCount) => tokenCount != null && tokenCount > 0) ||
    Object.keys(promptCostDetails ?? {}).length > 0 ||
    Object.keys(completionCostDetails ?? {}).length > 0;

  if (!hasOneSubTokenCount) {
    return children;
  }

  // Calculate cost percentages for header
  const totalCost = (promptCost ?? 0) + (completionCost ?? 0);
  const inputPercentage =
    totalCost > 0 ? ((promptCost ?? 0) / totalCost) * 100 : 0;
  const outputPercentage =
    totalCost > 0 ? ((completionCost ?? 0) / totalCost) * 100 : 0;
  const showCostHeader =
    promptCost != null &&
    completionCost != null &&
    !(completionCost === 0 && promptCost === 0);

  return totalTokens != null ? (
    <Tooltip
      side={side}
      align="start"
      tooltipClassName="max-w-[500px] min-w-[260px] p-0"
      title={
        <div className="flex flex-col divide-y divide-secondary rounded text-xs">
          {/* Cost breakdown header - only show if both costs are available */}
          {showCostHeader && (
            <div className="flex flex-col gap-2 px-4 py-3">
              <div className="flex justify-between gap-5 text-sm font-medium">
                <span className="text-xs font-medium text-tertiary">
                  INPUT COST
                </span>
                <span className="text-xs font-medium text-tertiary">
                  OUTPUT COST
                </span>
              </div>
              <div className="flex justify-between gap-5 text-sm font-medium">
                <span className="text-lg font-medium text-primary">
                  {inputPercentage.toFixed(2)}%{' '}
                  <span className="text-xs font-normal text-quaternary">
                    / {usdFormatter.format(promptCost)}
                  </span>
                </span>
                <span className="text-lg font-medium text-primary">
                  {outputPercentage.toFixed(2)}%{' '}
                  <span className="text-xs font-normal text-quaternary">
                    / {usdFormatter.format(completionCost)}
                  </span>
                </span>
              </div>
              <div className="h-[3px] w-full rounded-full">
                <div className="flex h-full">
                  <div
                    className="rounded-l-full bg-brand-primary"
                    style={{ width: `${inputPercentage}%` }}
                  />
                  <div
                    className="rounded-r-full bg-[#BCB2FD]"
                    style={{ width: `${outputPercentage}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {promptTokens != null && (
            <div className="flex flex-col gap-1 px-4 py-2">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-brand-primary"></div>
                <div className="text-xs font-medium text-secondary">INPUT</div>
                <div className="ml-auto flex items-center gap-1">
                  <TokenIcon />
                  <span className="text-xs font-medium text-secondary">
                    {formatter.format(promptTokens ?? 0)}
                    {promptCost != null && (
                      <span className="ml-1">
                        / {usdFormatter.format(promptCost)}
                      </span>
                    )}
                  </span>
                </div>
              </div>

              {(promptTokenDetails || promptCostDetails) == null &&
                audioTokens != null && (
                  <div className="ml-4 grid grid-cols-[auto,1fr] gap-4">
                    <div>audio</div>
                    <div className="text-right font-normal">
                      {formatter.format(audioTokens ?? 0)}
                    </div>
                  </div>
                )}

              {(promptTokenDetails || promptCostDetails) == null &&
                cacheReadTokens != null && (
                  <div className="ml-4 grid grid-cols-[auto,1fr] gap-4">
                    <div>cache read</div>
                    <div className="text-right font-normal">
                      {formatter.format(cacheReadTokens ?? 0)}
                    </div>
                  </div>
                )}

              {(promptTokenDetails || promptCostDetails) == null &&
                cacheCreationTokens != null && (
                  <div className="ml-4 grid grid-cols-[auto,1fr] gap-4">
                    <div>cache create</div>
                    <div className="text-right font-normal">
                      {formatter.format(cacheCreationTokens ?? 0)}
                    </div>
                  </div>
                )}

              {(promptTokenDetails || promptCostDetails) &&
                Object.keys({
                  ...(promptTokenDetails || {}),
                  ...(promptCostDetails || {}),
                }).map((tokenType) => (
                  <div
                    className="ml-4 grid grid-cols-[auto,1fr] gap-4"
                    key={tokenType}
                  >
                    <div className="text-xs font-normal text-tertiary">
                      {tokenType.replace(/_/g, ' ')}
                    </div>
                    <div className="text-right font-normal">
                      <span className="text-xs font-light text-quaternary">
                        {promptTokenDetails?.[tokenType] != null
                          ? formatter.format(promptTokenDetails[tokenType])
                          : '-'}
                      </span>
                      {promptCostDetails?.[tokenType] != null && (
                        <span className="ml-1 font-light opacity-60">
                          / {usdFormatter.format(promptCostDetails[tokenType])}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {completionTokens != null && (
            <div className="flex flex-col gap-1 px-4 py-2">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-[#BCB2FD]"></div>
                <div className="text-xs font-medium text-secondary">OUTPUT</div>
                <div className="ml-auto flex items-center gap-1">
                  <TokenIcon />
                  <span className="text-xs font-medium text-secondary">
                    {formatter.format(completionTokens ?? 0)}
                    {completionCost != null && (
                      <span className="ml-1">
                        / {usdFormatter.format(completionCost)}
                      </span>
                    )}
                  </span>
                </div>
              </div>

              {(completionTokenDetails || completionCostDetails) == null &&
                reasoningTokens != null && (
                  <div className="ml-4 grid grid-cols-[auto,1fr] gap-4">
                    <div>reasoning</div>
                    <div className="text-right font-normal">
                      {formatter.format(reasoningTokens ?? 0)}
                    </div>
                  </div>
                )}

              {(completionTokenDetails || completionCostDetails) &&
                Object.keys({
                  ...(completionTokenDetails || {}),
                  ...(completionCostDetails || {}),
                }).map((tokenType) => (
                  <div
                    className="ml-4 grid grid-cols-[auto,1fr] gap-4"
                    key={tokenType}
                  >
                    <div className="text-xs font-normal text-tertiary">
                      {tokenType.replace(/_/g, ' ')}
                    </div>
                    <div className="text-right text-xs font-light text-quaternary">
                      {completionTokenDetails?.[tokenType] != null
                        ? formatter.format(completionTokenDetails[tokenType])
                        : '-'}
                      {completionCostDetails?.[tokenType] != null && (
                        <span className="ml-1 text-xs font-light">
                          /{' '}
                          {usdFormatter.format(
                            completionCostDetails[tokenType]
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      }
    >
      {children}
    </Tooltip>
  ) : null;
}
